// Approved Document G water efficiency (regulation 36 of the Building Regulations 2010): the Appendix A water efficiency
// calculator for new dwellings and the section G2 fittings approach, shared by the server and the web (docs/PART-G.md).
// A port of Briary's Python water calculator (watercalc: calculation.py, standards.py), checked against the Approved
// Document (2015 edition incorporating 2016 and 2024 amendments):
//  - Table A1: litres/person/day = (capacity or flow rate x use factor) + fixed use, summed (5); less greywater (6) and
//    rainwater (7) savings; x normalisation factor 0.91 (9); + 5 l/p/d external use (10) = total (11).
//  - Tables A2.1 to A2.7: several fittings of one kind are averaged; the figure used is the greater of the average and
//    70% of the highest (paragraphs A8 to A10), except WCs, which use the average effective flushing volume.
//  - Table A3 water softeners, Tables A4.1 to A4.6 greywater, Tables A5.1 to A5.5 rainwater.
//  - Tables 2.1 (125 l/p/d) and 2.2 (110 l/p/d optional requirement): the fittings approach maxima.
// Values in the tables are rounded to two decimal places and the totals to one (paragraph A5), half away from zero as
// the published spreadsheet calculator does. Pure functions: no I/O.

// ---------------------------------------------------------------- policy (Approved Document G)

export type PartGCategory = 'wc' | 'basin_tap' | 'kitchen_tap' | 'bath' | 'shower' | 'washing_machine' | 'dishwasher' | 'waste_disposal_unit'

export const PARTG_CATEGORIES: PartGCategory[] = ['wc', 'basin_tap', 'bath', 'shower', 'kitchen_tap', 'washing_machine', 'dishwasher', 'waste_disposal_unit']

export const PARTG_CATEGORY_LABELS: Record<PartGCategory, string> = {
  wc: 'WC',
  basin_tap: 'Basin tap',
  kitchen_tap: 'Kitchen / utility sink tap',
  bath: 'Bath',
  shower: 'Shower',
  washing_machine: 'Washing machine',
  dishwasher: 'Dishwasher',
  waste_disposal_unit: 'Waste disposal unit',
}

/** The measure each category's value is in (Table A1 "Unit of measure"). */
export const PARTG_CATEGORY_UNITS: Record<PartGCategory, string> = {
  wc: 'Flush volume (litres)',
  basin_tap: 'Flow rate (litres/minute)',
  kitchen_tap: 'Flow rate (litres/minute)',
  bath: 'Capacity to overflow (litres)',
  shower: 'Flow rate (litres/minute)',
  washing_machine: 'Litres/kg dry load',
  dishwasher: 'Litres/place setting',
  waste_disposal_unit: 'Present',
}

/** Short units for tables and charts. */
export const PARTG_UNIT_SHORT: Record<PartGCategory, string> = {
  wc: 'litres',
  basin_tap: 'l/min',
  kitchen_tap: 'l/min',
  bath: 'litres',
  shower: 'l/min',
  washing_machine: 'l/kg',
  dishwasher: 'l/place setting',
  waste_disposal_unit: '',
}

/** Table A1 use factors. */
export const PARTG_USE_FACTORS = {
  wc_single_flush: 4.42,
  wc_dual_full_flush: 1.46,
  wc_dual_part_flush: 2.96,
  wc_multiple: 4.42,
  taps: 1.58,
  bath_with_shower: 0.11,
  shower_with_bath: 4.37,
  bath_only: 0.5,
  shower_only: 5.6,
  kitchen_taps: 0.44,
  washing_machine: 2.1,
  dishwasher: 3.6,
  waste_disposal_unit: 3.08,
  water_softener: 1.0,
} as const

/** Table A1 fixed use (litres/person/day). */
export const PARTG_FIXED_USE = { taps: 1.58, kitchen_taps: 10.36 } as const

export const PARTG_NORMALISATION_FACTOR = 0.91
export const PARTG_EXTERNAL_USE = 5.0
/** Table A2.7 note: effective flushing volume = full x 0.33 + part x 0.67. */
export const PARTG_DUAL_FLUSH_WEIGHTS = { full: 0.33, part: 0.67 } as const
/** Paragraphs A8 to A10: the proportionate figure is 70% of the highest fitting. */
export const PARTG_PROPORTIONATE_FACTOR = 0.7
/** Paragraph A2e-ii / A2f-ii: the figure to use where the appliance is not provided. */
export const PARTG_DEFAULT_DISHWASHER = 1.25
export const PARTG_DEFAULT_WASHING_MACHINE = 8.17
/** Paragraphs A12 to A15: a softener counts only above 4% of total capacity per regeneration. */
export const PARTG_SOFTENER_GOOD_PRACTICE_PERCENT = 4.0

export interface PartGTarget {
  key: string
  lpd: number
  title: string
  short: string
  authority: string
  status: string
  detail: string
  /** written excluding the 5 l/p/d external allowance: compared against row (9), not (11) */
  excludesExternal?: boolean
}

/** Statutory duties first, then the planning-policy levels commonly asked for in the UK. */
export const PARTG_TARGETS: PartGTarget[] = [
  {
    key: 'reg36_125', lpd: 125, short: '125 l/p/d (regulation 36(2)(a))',
    title: '125 litres/person/day: Building Regulations baseline (England)',
    authority: 'Regulation 36(2)(a), Building Regulations 2010; Approved Document G section G2',
    status: 'Statutory minimum in England',
    detail: 'The standard that applies to every new dwelling in England unless the optional requirement has been imposed. It includes the fixed 5 litres/person/day external water allowance.',
  },
  {
    key: 'reg36_110_optional', lpd: 110, short: '110 l/p/d (optional requirement)',
    title: '110 litres/person/day: regulation 36 optional requirement (England)',
    authority: 'Regulation 36(2)(b), Building Regulations 2010; Approved Document G paragraphs 2.8 to 2.12',
    status: 'Statutory where a planning condition imposes it',
    detail: 'Applies where a condition requiring the optional requirement was imposed when planning permission was granted. The person carrying out the work must inform the building control body that it applies.',
  },
  {
    key: 'wales_110', lpd: 110, short: '110 l/p/d (Wales)',
    title: '110 litres/person/day: Wales, new dwellings',
    authority: 'Regulation 36 as applied in Wales; Welsh Approved Document G',
    status: 'Statutory minimum in Wales for a newly erected dwelling',
    detail: 'In Wales 110 litres/person/day is the baseline for a newly erected dwelling. A dwelling formed by a material change of use is assessed against 125 litres/person/day.',
  },
  {
    key: 'london_105_internal', lpd: 105, short: '105 l/p/d internal (London Plan SI 5)', excludesExternal: true,
    title: '105 litres/person/day internal: London Plan Policy SI 5',
    authority: 'The London Plan, Policy SI 5 (water infrastructure)',
    status: 'Planning policy across Greater London',
    detail: 'Mains water consumption of 105 litres/person/day or less, excluding up to 5 litres/person/day for external water use. Compared against the normalised internal figure, row (9) of Table A1.',
  },
  {
    key: 'policy_105', lpd: 105, short: '105 l/p/d (local plan)',
    title: '105 litres/person/day total: proposed England baseline / local plan level',
    authority: 'Part G review consultation; local plan policy',
    status: 'Proposed future national baseline; used as a policy level',
    detail: 'Confirm what is in force at the date of the building notice or full plans application before relying on it as a regulatory duty rather than a planning or client requirement.',
  },
  {
    key: 'policy_100', lpd: 100, short: '100 l/p/d (water-stressed area)',
    title: '100 litres/person/day: seriously water-stressed area policy level',
    authority: 'Local plan policy; proposed replacement optional technical standard',
    status: 'Planning policy',
    detail: 'Applied by local planning authorities in seriously water-stressed catchments and in water-neutrality and nutrient-neutrality areas.',
  },
  {
    key: 'policy_95', lpd: 95, short: '95 l/p/d (enhanced)',
    title: '95 litres/person/day: enhanced local plan level',
    authority: 'Local plan policy / client sustainability brief',
    status: 'Planning policy or client requirement',
    detail: 'At this level rainwater or greywater re-use is usually needed alongside efficient fittings unless the appliance specification is unusually tight.',
  },
  {
    key: 'policy_90', lpd: 90, short: '90 l/p/d (high efficiency)',
    title: '90 litres/person/day: high water-efficiency policy level',
    authority: 'Local plan policy',
    status: 'Planning policy or voluntary level',
    detail: 'Compliance normally needs best-available fittings together with an alternative water source.',
  },
  {
    key: 'policy_80', lpd: 80, short: '80 l/p/d (water neutrality)',
    title: '80 litres/person/day: water-neutrality / exemplar level',
    authority: 'Local plan policy; water-neutrality schemes',
    status: 'Planning policy or exemplar requirement',
    detail: 'Cannot normally be reached from terminal fittings alone: needs rainwater harvesting or greywater recycling designed to BS 8515 or BS 8525.',
  },
]

export const partgTarget = (key: string): PartGTarget | null => PARTG_TARGETS.find((t) => t.key === key) ?? null

export interface PartGFittingsLimit {
  fitting: string
  maximum: string
  metric: 'wc' | 'shower' | 'bath' | 'basin_tap' | 'kitchen_tap' | 'dishwasher' | 'washing_machine'
  limit: number
  partLimit?: number
}

/** Table 2.1: maximum fittings consumption, 125 litres/person/day. */
export const PARTG_FITTINGS_APPROACH_125: PartGFittingsLimit[] = [
  { fitting: 'WC', maximum: '6/4 litres dual flush or 4.5 litres single flush', metric: 'wc', limit: 6, partLimit: 4 },
  { fitting: 'Shower', maximum: '10 l/min', metric: 'shower', limit: 10 },
  { fitting: 'Bath', maximum: '185 litres', metric: 'bath', limit: 185 },
  { fitting: 'Basin taps', maximum: '6 l/min', metric: 'basin_tap', limit: 6 },
  { fitting: 'Sink taps', maximum: '8 l/min', metric: 'kitchen_tap', limit: 8 },
  { fitting: 'Dishwasher', maximum: '1.25 l/place setting', metric: 'dishwasher', limit: 1.25 },
  { fitting: 'Washing machine', maximum: '8.17 l/kilogram', metric: 'washing_machine', limit: 8.17 },
]

/** Table 2.2: maximum fittings consumption, optional requirement level (110 litres/person/day). */
export const PARTG_FITTINGS_APPROACH_110: PartGFittingsLimit[] = [
  { fitting: 'WC', maximum: '4/2.6 litres dual flush', metric: 'wc', limit: 4, partLimit: 2.6 },
  { fitting: 'Shower', maximum: '8 l/min', metric: 'shower', limit: 8 },
  { fitting: 'Bath', maximum: '170 litres', metric: 'bath', limit: 170 },
  { fitting: 'Basin taps', maximum: '5 l/min', metric: 'basin_tap', limit: 5 },
  { fitting: 'Sink taps', maximum: '6 l/min', metric: 'kitchen_tap', limit: 6 },
  { fitting: 'Dishwasher', maximum: '1.25 l/place setting', metric: 'dishwasher', limit: 1.25 },
  { fitting: 'Washing machine', maximum: '8.17 l/kilogram', metric: 'washing_machine', limit: 8.17 },
]
const SINGLE_FLUSH_MAX_125 = 4.5

/** Where a drawn fitting has no stated figure, the calculation starts from the Table 2.2 maximum (to be confirmed). */
export const PARTG_SEED: Record<PartGCategory, { value: number; partFlush?: number; dualFlush?: boolean }> = {
  wc: { value: 4, partFlush: 2.6, dualFlush: true },
  basin_tap: { value: 5 },
  kitchen_tap: { value: 6 },
  bath: { value: 170 },
  shower: { value: 8 },
  washing_machine: { value: PARTG_DEFAULT_WASHING_MACHINE },
  dishwasher: { value: PARTG_DEFAULT_DISHWASHER },
  waste_disposal_unit: { value: 1 },
}

/** Words for the report's method sheet. */
export const PARTG_TEXT = {
  method: 'Water efficiency calculator for new dwellings',
  reference: 'Approved Document G (2015 edition incorporating 2016 and 2024 amendments), Appendix A, and regulation 36 of the Building Regulations 2010',
  scope:
    'The calculation assesses the estimated whole-dwelling consumption of wholesome water resulting from the design of the cold and hot water systems. It is not a design tool for water supply and drainage systems and it does not predict the actual consumption of the occupied dwelling, which also depends on how it is used (Approved Document G paragraph A1).',
  formula: [
    'Fitting consumption (l/p/d) = (capacity or flow rate x use factor) + fixed use',
    'Total calculated use (5) = sum of every fitting consumption',
    'Total water consumption (9) = [ (5) - greywater (6) - rainwater (7) ] x normalisation factor 0.91',
    'Total water consumption (11) = (9) + external water use (10) of 5.0 l/p/d',
  ],
  rounding:
    'Paragraph A5: every value in the calculator is rounded to two decimal places, except the total water consumption figures, which are rounded to one decimal place.',
  measurement:
    'Tap and shower flow rates are the full flow rate in litres per minute at a dynamic pressure of 3 ± 0.2 bar for high pressure (Type 1) systems, or 0.1 bar for low pressure (Type 2) systems, to BS EN 200 for taps and BS EN 1112 for shower outlets; shower flow is measured at the outlet on cold water. Bath capacity is the capacity to overflow, excluding the occupant’s displacement. For click taps and taps with a water break, the full flow rate is used.',
  occupancy:
    'Paragraphs A2h-iv, A15 and A22: two occupants in the first bedroom and one in each additional bedroom (two in a studio flat). Occupancy is used only for a water softener and any rainwater or greywater system.',
  excluded: [
    'Bidets are not included in the calculator (paragraph A2b).',
    'Bath taps are not included: bath use is covered by the bath use factor.',
    'External taps are not included: a fixed 5 litres/person/day external allowance applies instead (paragraph A2j).',
    'Spa hot tubs and swimming pools are not included, as they are not refilled daily (paragraph A4).',
    'Electromagnetic and other softeners that use no water are not included (paragraph A2h-v).',
  ],
  fittingsApproach: [
    'The fittings approach (paragraphs 2.5 to 2.7 and 2.10 to 2.12) may be used instead of the calculator where every fitting is within the Table 2.1 maximum (125 l/p/d) or the Table 2.2 maximum (110 l/p/d).',
    'It cannot be used where a shower is not provided, or where a waste disposal unit, a water softener or water re-use is provided.',
  ],
  notice:
    'Regulation 37 of the Building Regulations 2010 (and regulation 20 of the Building (Approved Inspectors etc.) Regulations) requires a notice of the calculated potential consumption of wholesome water per person per day for the dwelling as constructed to be given to the building control body, in most cases no later than five days after completion. A completion or final certificate is unlikely to be given until it has been received.',
  record:
    'Approved Document G section G2: a record of the sanitary appliances and white goods used in the calculation and installed in the dwelling, and of any alternative water sources, should be provided with enough information for the owner or occupier to maintain the water efficiency of the dwelling. The fittings schedule in this report is intended to form that record.',
  limitations: [
    'The calculation uses the manufacturer’s declared consumption of each fitting and appliance. Where a figure is marked as assumed, it must be confirmed (or the calculation re-issued) before the regulation 37 notice is given.',
    'The result relates only to the specification recorded in this report. Any substitution of a WC, tap, shower, bath or white good changes the result and needs the calculation to be re-issued.',
  ],
  alternativeSources: [
    'Greywater re-use systems: BS 8525 (paragraph A3a).',
    'Rainwater harvesting systems: BS 8515 (paragraphs A3b and A24).',
  ],
}

// ---------------------------------------------------------------- input

/** Where a fitting's consumption figure came from. */
export type PartGSource = 'manufacturer' | 'drawings' | 'sap' | 'assumed' | 'default'

export const PARTG_SOURCE_LABELS: Record<PartGSource, string> = {
  manufacturer: 'Manufacturer’s declared figure',
  drawings: 'Stated on the drawings / specification',
  sap: 'From the SAP assessment',
  assumed: 'Assumed: to be confirmed',
  default: 'Approved Document G default (appliance not provided)',
}

/** Where a fitting's position came from (for the "where the inputs came from" list). */
export type PartGOrigin = 'model' | 'sap' | 'assessor' | 'standard'

export const PARTG_ORIGIN_LABELS: Record<PartGOrigin, string> = {
  model: 'SketchUp model of the drawings',
  sap: 'SAP assessment',
  assessor: 'Entered by the assessor',
  standard: 'Standard assumption',
}

export interface PartGFitting {
  id: string
  category: PartGCategory
  /** e.g. "Bathroom WC" */
  reference: string
  /** the room, e.g. "Bathroom 1" */
  location: string
  /** the storey, e.g. "First" */
  level?: string
  /** manufacturer and model */
  product?: string
  quantity: number
  /** full flush volume (litres), flow rate (l/min), capacity to overflow (litres), l/kg, l/place setting; 1 for a WDU */
  value: number
  /** WC: the part flush volume of a dual-flush WC */
  partFlush?: number | null
  dualFlush?: boolean
  source: PartGSource
  origin?: PartGOrigin
  note?: string
}

export interface PartGSoftener {
  present: boolean
  /** percentage of total capacity used per regeneration */
  capacityUsedPercent: number
  litresPerRegeneration: number
  regenerationsPerDay: number
  /** 0: the dwelling's occupancy */
  occupantsServed: number
  product?: string
}

export interface PartGGreywater {
  present: boolean
  percentRecycled: number
  tapsSupplying: number
  showersSupplying: number
  bathsSupplying: number
  wcsUsing: number
  washingMachinesUsing: number
  reference?: string
}

export interface PartGRainwater {
  present: boolean
  /** BS 8515 intermediate (Table A5.1) or detailed (Table A5.2) */
  approach: 'intermediate' | 'detailed'
  collectionAreaM2: number
  yieldCoefficient: number
  annualRainfallMm: number
  dailyCollectionLitres: number
  wcsUsing: number
  washingMachinesUsing: number
  reference?: string
}

export interface PartGInput {
  version: 1
  bedrooms: number
  studio: boolean
  occupancyOverride: number | null
  targetKey: string
  /** a different target value from the chosen target's (e.g. a planning condition's), else null */
  targetLpd: number | null
  fittings: PartGFitting[]
  softener: PartGSoftener
  greywater: PartGGreywater
  rainwater: PartGRainwater
  /** the assessor's notes, printed on the report */
  notes: string
  preparedBy: string
}

let idSeq = 0
export const partgFittingId = () => `f${Date.now().toString(36)}${(idSeq++).toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`

export function partgNewFitting(category: PartGCategory, over: Partial<PartGFitting> = {}): PartGFitting {
  const seed = PARTG_SEED[category]
  return {
    id: partgFittingId(),
    category,
    reference: PARTG_CATEGORY_LABELS[category],
    location: '',
    quantity: 1,
    value: seed.value,
    partFlush: seed.partFlush ?? null,
    dualFlush: seed.dualFlush ?? false,
    source: category === 'washing_machine' || category === 'dishwasher' ? 'default' : category === 'waste_disposal_unit' ? 'drawings' : 'assumed',
    origin: 'assessor',
    ...over,
  }
}

export function partgDefaultInput(): PartGInput {
  return {
    version: 1,
    bedrooms: 0,
    studio: false,
    occupancyOverride: null,
    targetKey: 'reg36_125',
    targetLpd: null,
    fittings: [],
    softener: { present: false, capacityUsedPercent: 0, litresPerRegeneration: 0, regenerationsPerDay: 0, occupantsServed: 0 },
    greywater: { present: false, percentRecycled: 0, tapsSupplying: 0, showersSupplying: 0, bathsSupplying: 0, wcsUsing: 0, washingMachinesUsing: 0 },
    rainwater: { present: false, approach: 'intermediate', collectionAreaM2: 0, yieldCoefficient: 0.7, annualRainfallMm: 0, dailyCollectionLitres: 0, wcsUsing: 0, washingMachinesUsing: 0 },
    notes: '',
    preparedBy: '',
  }
}

const num = (v: unknown, d = 0, min = 0, max = 1e6) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' ? Number(v) : NaN
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : d
}
const int = (v: unknown, d = 0, min = 0, max = 1000) => Math.round(num(v, d, min, max))
const str = (v: unknown, max = 300) => (typeof v === 'string' ? v.slice(0, max) : '')
const obj = (v: unknown): Record<string, unknown> => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : {})
const SOURCES = Object.keys(PARTG_SOURCE_LABELS) as PartGSource[]
const ORIGINS = Object.keys(PARTG_ORIGIN_LABELS) as PartGOrigin[]

/** Any stored or posted JSON as a complete, sane input (unknown fields dropped, numbers clamped). */
export function partgNormaliseInput(raw: unknown): PartGInput {
  const r = obj(raw)
  const d = partgDefaultInput()
  const fittings: PartGFitting[] = (Array.isArray(r.fittings) ? r.fittings : []).slice(0, 200).flatMap((x) => {
    const f = obj(x)
    const category = f.category as PartGCategory
    if (!PARTG_CATEGORIES.includes(category)) return []
    const part = f.partFlush === null || f.partFlush === undefined || f.partFlush === '' ? null : num(f.partFlush, 0, 0, 50)
    return [{
      id: str(f.id, 60) || partgFittingId(),
      category,
      reference: str(f.reference, 200) || PARTG_CATEGORY_LABELS[category],
      location: str(f.location, 200),
      level: str(f.level, 60) || undefined,
      product: str(f.product, 300) || undefined,
      quantity: int(f.quantity, 1, 0, 100),
      value: num(f.value, PARTG_SEED[category].value, 0, 2000),
      partFlush: category === 'wc' ? part : null,
      dualFlush: category === 'wc' ? f.dualFlush === true : false,
      source: SOURCES.includes(f.source as PartGSource) ? (f.source as PartGSource) : 'assumed',
      origin: ORIGINS.includes(f.origin as PartGOrigin) ? (f.origin as PartGOrigin) : undefined,
      note: str(f.note, 1000) || undefined,
    }]
  })
  const s = obj(r.softener), g = obj(r.greywater), w = obj(r.rainwater)
  return {
    version: 1,
    bedrooms: int(r.bedrooms, 0, 0, 30),
    studio: r.studio === true,
    occupancyOverride: r.occupancyOverride === null || r.occupancyOverride === undefined || r.occupancyOverride === '' ? null : int(r.occupancyOverride, 0, 0, 50) || null,
    targetKey: partgTarget(str(r.targetKey, 60)) ? str(r.targetKey, 60) : d.targetKey,
    targetLpd: r.targetLpd === null || r.targetLpd === undefined || r.targetLpd === '' ? null : num(r.targetLpd, 0, 0, 500) || null,
    fittings,
    softener: { present: s.present === true, capacityUsedPercent: num(s.capacityUsedPercent, 0, 0, 100), litresPerRegeneration: num(s.litresPerRegeneration, 0, 0, 10000), regenerationsPerDay: num(s.regenerationsPerDay, 0, 0, 100), occupantsServed: int(s.occupantsServed, 0, 0, 50), product: str(s.product) || undefined },
    greywater: { present: g.present === true, percentRecycled: num(g.percentRecycled, 0, 0, 100), tapsSupplying: int(g.tapsSupplying), showersSupplying: int(g.showersSupplying), bathsSupplying: int(g.bathsSupplying), wcsUsing: int(g.wcsUsing), washingMachinesUsing: int(g.washingMachinesUsing), reference: str(g.reference) || undefined },
    rainwater: {
      present: w.present === true, approach: w.approach === 'detailed' ? 'detailed' : 'intermediate',
      collectionAreaM2: num(w.collectionAreaM2, 0, 0, 100000), yieldCoefficient: num(w.yieldCoefficient, 0.7, 0, 1), annualRainfallMm: num(w.annualRainfallMm, 0, 0, 10000),
      dailyCollectionLitres: num(w.dailyCollectionLitres, 0, 0, 100000), wcsUsing: int(w.wcsUsing), washingMachinesUsing: int(w.washingMachinesUsing), reference: str(w.reference) || undefined,
    },
    notes: str(r.notes, 4000),
    preparedBy: str(r.preparedBy, 200),
  }
}

/** Two occupants in the first bedroom and one in each additional bedroom; two in a studio. */
export function partgOccupancy(input: Pick<PartGInput, 'bedrooms' | 'studio' | 'occupancyOverride'>): number {
  if (input.occupancyOverride && input.occupancyOverride > 0) return Math.round(input.occupancyOverride)
  if (input.studio) return 2
  if (input.bedrooms <= 0) return 0
  return 2 + Math.max(0, input.bedrooms - 1)
}

export const partgIsDualFlush = (f: PartGFitting) => f.category === 'wc' && !!f.dualFlush && (f.partFlush ?? 0) > 0

/** Table A2.7: the effective flushing volume of a WC. */
export const partgEffectiveFlush = (f: PartGFitting) =>
  partgIsDualFlush(f) ? f.value * PARTG_DUAL_FLUSH_WEIGHTS.full + (f.partFlush ?? 0) * PARTG_DUAL_FLUSH_WEIGHTS.part : f.value

// ---------------------------------------------------------------- arithmetic

/** Rounds half away from zero (the spreadsheet calculator's ROUND), robust to binary representation (1.005 → 1.01). */
export function partgRound(v: number, digits: number): number {
  if (!Number.isFinite(v)) return 0
  const p = 10 ** digits
  const x = Math.abs(v) * p
  const r = Math.round(x + 1e-9 * Math.max(1, x))
  return (Math.sign(v) * r) / p || 0
}
const r2 = (v: number) => partgRound(v, 2)
const r1 = (v: number) => partgRound(v, 1)
const EPS = 1e-9

// ---------------------------------------------------------------- result

export interface PartGLine {
  key: string
  /** the category the line belongs to (chart grouping); water_softener for the softener */
  group: PartGCategory | 'water_softener'
  installationType: string
  unit: string
  /** column (1) */
  capacity: number
  /** column (2) */
  useFactor: number
  /** column (3) */
  fixedUse: number
  /** column (4) = (1) x (2) + (3) */
  lpd: number
  basis: string
}

export interface PartGAveragingRow { reference: string; value: number; quantity: number; total: number }

/** Tables A2.1 to A2.7. */
export interface PartGAveraging {
  category: PartGCategory
  table: string
  unit: string
  rows: PartGAveragingRow[]
  totalQuantity: number
  totalValue: number
  average: number
  highest: number
  /** 70% of the highest (not for WCs) */
  proportionate: number | null
  adopted: number
  proportionateApplies: boolean
  rule: string
}

export interface PartGSoftenerResult {
  present: boolean
  capacityUsedPercent: number
  litresPerRegeneration: number
  regenerationsPerDay: number
  occupantsServed: number
  beyondGoodPractice: number
  lpd: number
  note: string
}

export interface PartGWorkRow { label: string; value: string }

export interface PartGGreywaterResult {
  present: boolean
  collected: number
  percentRecycled: number
  available: number
  demand: number
  saving: number
  collectionRows: PartGWorkRow[]
  demandRows: PartGWorkRow[]
  note: string
}

export interface PartGRainwaterResult {
  present: boolean
  approach: string
  collected: number
  demand: number
  saving: number
  collectionRows: PartGWorkRow[]
  demandRows: PartGWorkRow[]
  note: string
}

export interface PartGFittingsApproachRow {
  fitting: string
  maximum: string
  actual: string
  status: 'within' | 'above' | 'not_provided' | 'missing'
}

export interface PartGFittingsApproach {
  /** the approach is open to this dwelling (a shower, no WDU, softener or re-use) */
  available: boolean
  passes: boolean
  table: '2.1' | '2.2' | null
  label: string
  /** the notice wording where it passes */
  statement: string | null
  rows: PartGFittingsApproachRow[]
  blocking: string[]
}

export interface PartGSensitivityRow {
  category: PartGCategory
  description: string
  from: string
  to: string
  total: number
  change: number
  complies: boolean
}

export interface PartGTargetCheck {
  key: string
  title: string
  short: string
  lpd: number
  excludesExternal: boolean
  assessed: number
  met: boolean
  margin: number
}

/** Each category's share of the total, for charts. */
export interface PartGShare { group: PartGCategory | 'water_softener'; label: string; lpd: number }

export interface PartGResult {
  occupancy: number
  target: PartGTarget & { value: number; custom: boolean }
  lines: PartGLine[]
  averaging: PartGAveraging[]
  softener: PartGSoftenerResult
  greywater: PartGGreywaterResult
  rainwater: PartGRainwaterResult
  /** (5) */
  totalCalculatedUse: number
  /** (6) */
  greywaterSaving: number
  /** (7) */
  rainwaterSaving: number
  /** (8) */
  normalisationFactor: number
  /** (9) */
  normalised: number
  /** (10) */
  external: number
  /** (11) */
  total: number
  /** the figure compared with the target: (11), or (9) for a target written excluding external use */
  assessed: number
  excludesExternal: boolean
  complies: boolean
  /** target - assessed */
  headroom: number
  fittingsApproach: PartGFittingsApproach
  sensitivity: PartGSensitivityRow[]
  targets: PartGTargetCheck[]
  shares: PartGShare[]
  bathOnly: boolean
  showerOnly: boolean
  /** fittings whose figure is assumed or not confirmed */
  unconfirmed: number
  warnings: string[]
}

const present = (fs: PartGFitting[], c: PartGCategory) => fs.filter((f) => f.category === c && f.quantity > 0)

const AVERAGING_TABLE: Record<PartGCategory, string> = {
  basin_tap: 'Table A2.1', kitchen_tap: 'Table A2.3', bath: 'Table A2.4', shower: 'Table A2.2',
  washing_machine: 'Table A2.6', dishwasher: 'Table A2.5', wc: 'Table A2.7', waste_disposal_unit: '',
}

/** Tables A2.1 to A2.7: fittings of one value are one type (their quantities added). */
function averaging(fs: PartGFitting[], c: PartGCategory): PartGAveraging | null {
  const list = present(fs, c)
  if (!list.length) return null
  const wc = c === 'wc'
  const types = new Map<string, PartGAveragingRow>()
  for (const f of list) {
    const value = r2(wc ? partgEffectiveFlush(f) : f.value)
    const key = wc ? `${r2(f.value)}|${r2(f.partFlush ?? 0)}|${partgIsDualFlush(f)}` : String(value)
    const t = types.get(key)
    if (t) {
      t.quantity += f.quantity
      if (!t.reference.split(', ').includes(f.reference)) t.reference = `${t.reference}, ${f.reference}`
    } else types.set(key, { reference: f.reference || PARTG_CATEGORY_LABELS[c], value, quantity: f.quantity, total: 0 })
  }
  const rows = [...types.values()].map((t) => ({ ...t, total: r2(t.value * t.quantity) }))
  const totalQuantity = rows.reduce((s, x) => s + x.quantity, 0)
  const totalValue = r2(rows.reduce((s, x) => s + x.total, 0))
  const average = totalQuantity ? r2(totalValue / totalQuantity) : 0
  const highest = r2(Math.max(...rows.map((x) => x.value)))
  if (wc) {
    return {
      category: c, table: AVERAGING_TABLE[c], unit: 'Effective flushing volume (litres)', rows, totalQuantity, totalValue, average, highest,
      proportionate: null, adopted: average, proportionateApplies: false,
      rule: 'Paragraph A11 and Table A2.7: the average effective flushing volume of the WC types provided (full x 0.33 + part x 0.67 for a dual flush). The proportionate step does not apply to WCs.',
    }
  }
  const proportionate = r2(highest * PARTG_PROPORTIONATE_FACTOR)
  return {
    category: c, table: AVERAGING_TABLE[c], unit: PARTG_CATEGORY_UNITS[c], rows, totalQuantity, totalValue, average, highest,
    proportionate, adopted: r2(Math.max(average, proportionate)), proportionateApplies: proportionate > average,
    rule: 'Paragraphs A8 to A10: the figure used is the greater of the average and 70 per cent of the highest fitting.',
  }
}

function line(key: string, group: PartGLine['group'], installationType: string, unit: string, capacity: number, useFactor: number, fixedUse: number, basis: string): PartGLine {
  const c = r2(capacity)
  return { key, group, installationType, unit, capacity: c, useFactor, fixedUse, lpd: r2(c * useFactor + fixedUse), basis }
}

function softenerResult(input: PartGInput, occupancy: number): PartGSoftenerResult {
  const s = input.softener
  const base = { present: s.present, capacityUsedPercent: r2(s.capacityUsedPercent), litresPerRegeneration: r2(s.litresPerRegeneration), regenerationsPerDay: r2(s.regenerationsPerDay), occupantsServed: s.occupantsServed || occupancy || 1 }
  if (!s.present) return { ...base, occupantsServed: 0, beyondGoodPractice: 0, lpd: 0, note: 'No ion-exchange water softener is specified, so zero is entered in Table A1.' }
  if (s.capacityUsedPercent <= 0)
    return { ...base, beyondGoodPractice: 0, lpd: 0, note: 'A softener is specified but the percentage of total capacity used per regeneration has not been given, so no consumption could be calculated: obtain the manufacturer’s figure for the local water hardness.' }
  const beyond = Math.max(0, (1 - PARTG_SOFTENER_GOOD_PRACTICE_PERCENT / s.capacityUsedPercent) * (s.litresPerRegeneration * s.regenerationsPerDay))
  const note = s.capacityUsedPercent <= PARTG_SOFTENER_GOOD_PRACTICE_PERCENT
    ? `The softener uses ${r2(s.capacityUsedPercent)}% of total capacity per regeneration, at or below the 4% good-practice benchmark, so zero is entered in Table A1 (paragraph A13).`
    : 'Table A3: only the water used beyond the 4% of total capacity good-practice benchmark counts: [1 - (4 / % used)] x litres per regeneration x regenerations per day, divided by the occupants served.'
  return { ...base, beyondGoodPractice: r2(beyond), lpd: r2(beyond / base.occupantsServed), note }
}

/** Tables A4.1, A4.2, A5.3, A5.4: demand = [sum(value x quantity served) / total present] x use factor. */
function proportionalDemand(fs: PartGFitting[], c: PartGCategory, served: number, useFactor: number, rows: PartGWorkRow[], title: string) {
  const list = present(fs, c)
  const total = list.reduce((s, f) => s + f.quantity, 0)
  if (!list.length || total <= 0 || served <= 0) return 0
  rows.push({ label: title, value: '' })
  let remaining = Math.min(served, total)
  let sum = 0
  for (const f of list) {
    const value = c === 'wc' ? partgEffectiveFlush(f) : f.value
    const using = Math.min(f.quantity, remaining)
    remaining -= using
    sum += value * using
    rows.push({ label: f.reference || PARTG_CATEGORY_LABELS[c], value: `${r2(value)} x ${using} of ${f.quantity} present = ${r2(value * using)}` })
  }
  const demand = (sum / total) * useFactor
  rows.push({ label: 'Average demand', value: `[${r2(sum)} / ${total}] x ${useFactor} = ${r2(demand)} l/p/d` })
  return r2(demand)
}

/** Tables A4.3 to A4.5: greywater collected from a proportion of the fittings. */
function proportionalSupply(fs: PartGFitting[], c: PartGCategory, supplying: number, useFactor: number, fixed: number, rows: PartGWorkRow[], title: string) {
  const list = present(fs, c)
  const total = list.reduce((s, f) => s + f.quantity, 0)
  if (!list.length || total <= 0 || supplying <= 0) return 0
  rows.push({ label: title, value: '' })
  let remaining = Math.min(supplying, total)
  let sum = 0
  for (const f of list) {
    const using = Math.min(f.quantity, remaining)
    remaining -= using
    sum += f.value * using
    rows.push({ label: f.reference || PARTG_CATEGORY_LABELS[c], value: `${r2(f.value)} x ${using} of ${f.quantity} present = ${r2(f.value * using)}` })
  }
  const supply = (sum / total) * useFactor + fixed
  rows.push({ label: 'Average supply', value: `[${r2(sum)} / ${total}] x ${useFactor}${fixed ? ` + ${fixed}` : ''} = ${r2(supply)} l/p/d` })
  return r2(supply)
}

function greywaterResult(input: PartGInput, bathOnly: boolean, showerOnly: boolean): PartGGreywaterResult {
  const g = input.greywater
  if (!g.present) return { present: false, collected: 0, percentRecycled: 0, available: 0, demand: 0, saving: 0, collectionRows: [], demandRows: [], note: 'No greywater re-use system is specified.' }
  const fs = input.fittings
  const collectionRows: PartGWorkRow[] = []
  const demandRows: PartGWorkRow[] = []
  let collected = proportionalSupply(fs, 'basin_tap', g.tapsSupplying, PARTG_USE_FACTORS.taps, PARTG_FIXED_USE.taps, collectionRows, 'Table A4.3: basin taps')
  collected += proportionalSupply(fs, 'shower', g.showersSupplying, showerOnly ? PARTG_USE_FACTORS.shower_only : PARTG_USE_FACTORS.shower_with_bath, 0, collectionRows, 'Table A4.4: showers')
  collected += proportionalSupply(fs, 'bath', g.bathsSupplying, bathOnly ? PARTG_USE_FACTORS.bath_only : PARTG_USE_FACTORS.bath_with_shower, 0, collectionRows, 'Table A4.5: baths')
  const demand = r2(
    proportionalDemand(fs, 'wc', g.wcsUsing, PARTG_USE_FACTORS.wc_multiple, demandRows, 'Table A4.1: WCs') +
    proportionalDemand(fs, 'washing_machine', g.washingMachinesUsing, PARTG_USE_FACTORS.washing_machine, demandRows, 'Table A4.2: washing machines'),
  )
  const available = r2(collected * (g.percentRecycled / 100))
  return {
    present: true, collected: r2(collected), percentRecycled: r2(g.percentRecycled), available, demand, saving: r2(Math.min(available, demand)), collectionRows, demandRows,
    note: 'Table A4.6: where the greywater available is greater than the demand of the fittings it serves, the saving is limited to the demand; otherwise it is limited to the volume available.',
  }
}

function rainwaterResult(input: PartGInput, occupancy: number): PartGRainwaterResult {
  const w = input.rainwater
  if (!w.present) return { present: false, approach: '', collected: 0, demand: 0, saving: 0, collectionRows: [], demandRows: [], note: 'No rainwater harvesting system is specified.' }
  const occupants = occupancy || 1
  const collectionRows: PartGWorkRow[] = []
  let daily: number
  if (w.approach === 'detailed') {
    daily = w.dailyCollectionLitres
    collectionRows.push({ label: 'Table A5.2 (a) daily rainwater collection', value: `${r2(daily)} litres` }, { label: '(b) number of occupants', value: String(occupants) })
  } else {
    daily = (w.collectionAreaM2 * w.yieldCoefficient * w.annualRainfallMm) / 365
    collectionRows.push(
      { label: 'Table A5.1 (a) collection area', value: `${r2(w.collectionAreaM2)} m²` },
      { label: '(b) yield coefficient and hydraulic filter efficiency', value: `${r2(w.yieldCoefficient)}` },
      { label: '(c) rainfall', value: `${r2(w.annualRainfallMm)} mm/year` },
      { label: '(d) daily rainwater collection = [(a) x (b) x (c)] / 365', value: `${r2(daily)} litres` },
      { label: '(e) number of occupants', value: String(occupants) },
    )
  }
  const perPerson = r2(daily / occupants)
  collectionRows.push({ label: 'Daily rainwater per person', value: `${perPerson} l/p/d` })
  const demandRows: PartGWorkRow[] = []
  const demand = r2(
    proportionalDemand(input.fittings, 'wc', w.wcsUsing, PARTG_USE_FACTORS.wc_multiple, demandRows, 'Table A5.3: WCs') +
    proportionalDemand(input.fittings, 'washing_machine', w.washingMachinesUsing, PARTG_USE_FACTORS.washing_machine, demandRows, 'Table A5.4: washing machines'),
  )
  return {
    present: true, approach: w.approach === 'detailed' ? 'BS 8515 detailed approach' : 'BS 8515 intermediate approach',
    collected: perPerson, demand, saving: r2(Math.min(perPerson, demand)), collectionRows, demandRows,
    note: 'Table A5.5: where the volume collected is greater than the demand of the fittings it serves, the saving is limited to the demand. Storage sizing and all other design follow BS 8515 (paragraph A24).',
  }
}

function fittingsApproach(input: PartGInput, targetValue: number): PartGFittingsApproach {
  const fs = input.fittings
  const blocking: string[] = []
  if (!present(fs, 'shower').length) blocking.push('A shower is not provided, so the fittings approach cannot be used.')
  if (input.softener.present) blocking.push('A water softener is provided, so the fittings approach cannot be used.')
  if (present(fs, 'waste_disposal_unit').length) blocking.push('A waste disposal unit is provided, so the fittings approach cannot be used.')
  if (input.greywater.present || input.rainwater.present) blocking.push('Water re-use is provided, so the fittings approach cannot be used.')
  let limits: PartGFittingsLimit[], table: '2.1' | '2.2', label: string, singleMax: number | null
  if (targetValue >= 125) {
    limits = PARTG_FITTINGS_APPROACH_125; table = '2.1'; label = 'Table 2.1: 125 litres/person/day'; singleMax = SINGLE_FLUSH_MAX_125
  } else if (targetValue >= 110) {
    limits = PARTG_FITTINGS_APPROACH_110; table = '2.2'; label = 'Table 2.2: 110 litres/person/day optional requirement'; singleMax = null
  } else {
    return {
      available: false, passes: false, table: null, label: 'Not applicable', statement: null, rows: [],
      blocking: ['Approved Document G publishes a fittings approach only for the 125 and 110 litres/person/day levels: a lower target is shown with the full calculator.'],
    }
  }
  const worst = (c: PartGCategory) => {
    const list = present(fs, c)
    return list.length ? Math.max(...list.map((f) => f.value)) : null
  }
  const rows: PartGFittingsApproachRow[] = []
  let passes = true
  for (const l of limits) {
    if (l.metric === 'wc') {
      const wcs = present(fs, 'wc')
      if (!wcs.length) {
        rows.push({ fitting: l.fitting, maximum: l.maximum, actual: 'Not specified', status: 'missing' })
        passes = false
        continue
      }
      // every WC must be within the maximum: a dual flush against the pair, a single flush against its own figure
      const fails = wcs.filter((f) =>
        partgIsDualFlush(f) ? !(f.value <= l.limit + EPS && (f.partFlush ?? 0) <= (l.partLimit ?? 0) + EPS) : !(singleMax !== null && f.value <= singleMax + EPS),
      )
      const shown = fails[0] ?? wcs.reduce((a, b) => (partgEffectiveFlush(b) > partgEffectiveFlush(a) ? b : a))
      rows.push({
        fitting: l.fitting, maximum: l.maximum,
        actual: partgIsDualFlush(shown) ? `${r2(shown.value)} / ${r2(shown.partFlush ?? 0)} litres dual flush` : `${r2(shown.value)} litres single flush`,
        status: fails.length ? 'above' : 'within',
      })
      passes = passes && !fails.length
      continue
    }
    const v = worst(l.metric)
    if (v === null) {
      rows.push({ fitting: l.fitting, maximum: l.maximum, actual: 'Not provided', status: 'not_provided' })
      continue
    }
    const ok = v <= l.limit + EPS
    rows.push({ fitting: l.fitting, maximum: l.maximum, actual: `${r2(v)} ${PARTG_UNIT_SHORT[l.metric]}`, status: ok ? 'within' : 'above' })
    passes = passes && ok
  }
  const ok = passes && !blocking.length
  return {
    available: !blocking.length, passes: ok, table, label, rows, blocking,
    statement: ok ? `Less than ${table === '2.1' ? 125 : 110} litres/person/day using fittings approach` : null,
  }
}

interface Core {
  lines: PartGLine[]
  averaging: PartGAveraging[]
  softener: PartGSoftenerResult
  greywater: PartGGreywaterResult
  rainwater: PartGRainwaterResult
  totalCalculatedUse: number
  normalised: number
  total: number
  warnings: string[]
  bathOnly: boolean
  showerOnly: boolean
}

/** Table A1 lines and totals. */
function core(input: PartGInput, occupancy: number): Core {
  const fs = input.fittings
  const lines: PartGLine[] = []
  const avg: PartGAveraging[] = []
  const warnings: string[] = []
  const F = PARTG_USE_FACTORS

  // WCs: one type is entered directly; several types use the Table A2.7 average effective flushing volume
  const wc = averaging(fs, 'wc')
  if (!wc) warnings.push('No WC is specified: every dwelling needs at least one WC and its flush volume.')
  else if (wc.rows.length === 1) {
    const f = present(fs, 'wc')[0]
    if (partgIsDualFlush(f)) {
      lines.push(line('wc_dual_full_flush', 'wc', 'WC (dual flush): full flush', 'Full flush volume (litres)', f.value, F.wc_dual_full_flush, 0, 'Table A1 "WC (dual flush)": full flush volume x 1.46.'))
      lines.push(line('wc_dual_part_flush', 'wc', 'WC (dual flush): part flush', 'Part flush volume (litres)', f.partFlush ?? 0, F.wc_dual_part_flush, 0, 'Table A1 "WC (dual flush)": part flush volume x 2.96.'))
    } else lines.push(line('wc_single_flush', 'wc', 'WC (single flush)', 'Flush volume (litres)', f.value, F.wc_single_flush, 0, 'Table A1 "WC (single flush)": flush volume x 4.42.'))
  } else {
    avg.push(wc)
    lines.push(line('wc_multiple', 'wc', 'WCs (multiple fittings)', 'Average effective flushing volume (litres)', wc.adopted, F.wc_multiple, 0, 'Table A1 "WCs (multiple fittings)": the Table A2.7 average effective flushing volume x 4.42.'))
  }

  const basin = averaging(fs, 'basin_tap')
  if (!basin) warnings.push('No basin taps are specified: the taps row of Table A1 must be completed.')
  else {
    if (basin.rows.length > 1) avg.push(basin)
    lines.push(line('taps', 'basin_tap', 'Taps (excluding kitchen/utility room taps)', 'Flow rate (litres/minute)', basin.adopted, F.taps, PARTG_FIXED_USE.taps, 'Table A1 "Taps": flow rate x 1.58 plus a fixed 1.58 l/p/d.'))
  }

  const baths = averaging(fs, 'bath')
  const showers = averaging(fs, 'shower')
  const bathOnly = !!baths && !showers
  const showerOnly = !!showers && !baths
  if (!baths && !showers) warnings.push('Neither a bath nor a shower is specified: a dwelling is expected to have at least one.')
  if (baths) {
    if (baths.rows.length > 1) avg.push(baths)
    lines.push(bathOnly
      ? line('bath_only', 'bath', 'Bath only', 'Capacity to overflow (litres)', baths.adopted, F.bath_only, 0, 'Table A1 "Bath only": capacity to overflow x 0.50.')
      : line('bath_with_shower', 'bath', 'Bath (where shower also present)', 'Capacity to overflow (litres)', baths.adopted, F.bath_with_shower, 0, 'Table A1 "Bath (where shower also present)": capacity to overflow x 0.11.'))
  }
  if (showers) {
    if (showers.rows.length > 1) avg.push(showers)
    lines.push(showerOnly
      ? line('shower_only', 'shower', 'Shower only', 'Flow rate (litres/minute)', showers.adopted, F.shower_only, 0, 'Table A1 "Shower only": flow rate x 5.60.')
      : line('shower_with_bath', 'shower', 'Shower (where bath also present)', 'Flow rate (litres/minute)', showers.adopted, F.shower_with_bath, 0, 'Table A1 "Shower (where bath also present)": flow rate x 4.37.'))
  }

  const kitchen = averaging(fs, 'kitchen_tap')
  if (!kitchen) warnings.push('No kitchen or utility sink tap is specified: the kitchen row of Table A1 carries a fixed 10.36 l/p/d and cannot be left out.')
  else {
    if (kitchen.rows.length > 1) avg.push(kitchen)
    lines.push(line('kitchen_taps', 'kitchen_tap', 'Kitchen/utility room sink taps', 'Flow rate (litres/minute)', kitchen.adopted, F.kitchen_taps, PARTG_FIXED_USE.kitchen_taps, 'Table A1 "Kitchen/utility room sink taps": flow rate x 0.44 plus a fixed 10.36 l/p/d.'))
  }

  const machines = averaging(fs, 'washing_machine')
  if (!machines) warnings.push('No washing machine line: where none is provided, 8.17 litres/kg must be assumed (paragraph A2f-ii).')
  else {
    if (machines.rows.length > 1) avg.push(machines)
    lines.push(line('washing_machine', 'washing_machine', 'Washing machine', 'Litres/kg dry load', machines.adopted, F.washing_machine, 0, 'Table A1 "Washing machine": litres/kg dry load x 2.1 (EU energy label annual water use / (220 x capacity in kg)).'))
  }
  const dish = averaging(fs, 'dishwasher')
  if (!dish) warnings.push('No dishwasher line: where none is provided, 1.25 litres/place setting must be assumed (paragraph A2e-ii).')
  else {
    if (dish.rows.length > 1) avg.push(dish)
    lines.push(line('dishwasher', 'dishwasher', 'Dishwasher', 'Litres/place setting', dish.adopted, F.dishwasher, 0, 'Table A1 "Dishwasher": litres/place setting x 3.6 (EU energy label annual water use / (280 x place settings)).'))
  }

  if (present(fs, 'waste_disposal_unit').length)
    lines.push({ key: 'waste_disposal_unit', group: 'waste_disposal_unit', installationType: 'Waste disposal unit', unit: 'Present = 1', capacity: 1, useFactor: F.waste_disposal_unit, fixedUse: 0, lpd: r2(F.waste_disposal_unit), basis: 'Table A1 "Waste disposal unit": 3.08 l/p/d where one is present (paragraph A2i).' })

  const softener = softenerResult(input, occupancy)
  if (softener.present) {
    lines.push({ key: 'water_softener', group: 'water_softener', installationType: 'Water softener', unit: 'Litres/person/day', capacity: softener.lpd, useFactor: F.water_softener, fixedUse: 0, lpd: r2(softener.lpd), basis: 'Table A1 "Water softener": from the Table A3 consumption beyond the 4% good-practice benchmark.' })
    if (input.softener.capacityUsedPercent <= 0) warnings.push(softener.note)
  }

  const greywater = greywaterResult(input, bathOnly, showerOnly)
  const rainwater = rainwaterResult(input, occupancy)
  const totalCalculatedUse = r2(lines.reduce((s, l) => s + l.lpd, 0))
  const normalised = r1((totalCalculatedUse - greywater.saving - rainwater.saving) * PARTG_NORMALISATION_FACTOR)
  const total = r1(normalised + PARTG_EXTERNAL_USE)
  return { lines, averaging: avg, softener, greywater, rainwater, totalCalculatedUse, normalised, total, warnings, bathOnly, showerOnly }
}

const SENSITIVITY: [PartGCategory, string, number, string][] = [
  ['wc', 'WC full flush volume', 0.5, 'litres'],
  ['basin_tap', 'basin tap flow rate', 1, 'l/min'],
  ['shower', 'shower flow rate', 1, 'l/min'],
  ['kitchen_tap', 'kitchen sink tap flow rate', 1, 'l/min'],
  ['bath', 'bath capacity to overflow', 10, 'litres'],
  ['washing_machine', 'washing machine water use', 1, 'l/kg'],
  ['dishwasher', 'dishwasher water use', 0.25, 'l/place setting'],
]

function adjusted(input: PartGInput, c: PartGCategory, delta: number): PartGInput {
  return {
    ...input,
    fittings: input.fittings.map((f) => {
      if (f.category !== c) return f
      const n = { ...f, value: Math.max(0, f.value + delta) }
      // a dual-flush WC is a pair: move both together so the step is a realistic product change
      if (c === 'wc' && f.partFlush) n.partFlush = Math.max(0, f.partFlush + delta * 0.65)
      return n
    }),
  }
}

const SHARE_LABELS: Record<PartGShare['group'], string> = { ...PARTG_CATEGORY_LABELS, basin_tap: 'Basin taps', kitchen_tap: 'Kitchen / utility taps', wc: 'WCs', water_softener: 'Water softener' }

/** The whole assessment: Table A1, the target, the fittings approach, every other target and the sensitivity. */
export function calculatePartG(raw: PartGInput): PartGResult {
  const input = partgNormaliseInput(raw)
  const occupancy = partgOccupancy(input)
  const t = partgTarget(input.targetKey) ?? PARTG_TARGETS[0]
  const custom = input.targetLpd !== null && input.targetLpd > 0 && Math.abs(input.targetLpd - t.lpd) > EPS
  const value = custom ? input.targetLpd! : t.lpd
  const excludesExternal = !!t.excludesExternal
  const c = core(input, occupancy)
  const compare = (x: Core, excl: boolean) => (excl ? x.normalised : x.total)
  const assessed = compare(c, excludesExternal)
  const complies = assessed <= value + EPS
  const warnings = [...c.warnings]

  const unconfirmed = input.fittings.filter((f) => f.quantity > 0 && (f.source === 'assumed' || f.source === 'default')).length
  const assumed = input.fittings.filter((f) => f.quantity > 0 && f.source === 'assumed').length
  if (assumed)
    warnings.push(`${assumed} fitting line${assumed === 1 ? ' is' : 's are'} assumed rather than a manufacturer’s declared figure: confirm ${assumed === 1 ? 'it' : 'them'} before the regulation 37 notice is given (Approved Document G paragraph 2.1).`)
  if (input.bedrooms <= 0 && !input.studio && !input.occupancyOverride && (input.softener.present || input.rainwater.present || input.greywater.present))
    warnings.push('The number of bedrooms is not set: it gives the occupancy used for the water softener, rainwater and greywater calculations.')
  if (input.greywater.present && input.rainwater.present && (Math.min(input.greywater.wcsUsing, input.rainwater.wcsUsing) > 0 || Math.min(input.greywater.washingMachinesUsing, input.rainwater.washingMachinesUsing) > 0))
    warnings.push('Greywater and rainwater are both recorded as serving the same fittings: each saving is deducted separately, so the same demand must not be claimed twice.')

  const sensitivity: PartGSensitivityRow[] = []
  for (const [cat, description, step, unit] of SENSITIVITY) {
    const list = present(input.fittings, cat)
    if (!list.length) continue
    const current = Math.max(...list.map((f) => f.value))
    for (const d of [step, -step]) {
      if (d < 0 && current + d <= 0) continue
      const total = compare(core(adjusted(input, cat, d), occupancy), excludesExternal)
      sensitivity.push({
        category: cat, description: `${d > 0 ? 'Increase' : 'Reduce'} the ${description} by ${step} ${unit}`,
        from: `${r2(current)} ${unit}`, to: `${r2(current + d)} ${unit}`, total, change: r1(total - assessed), complies: total <= value + EPS,
      })
    }
  }

  const targets: PartGTargetCheck[] = PARTG_TARGETS.map((x) => {
    const a = compare(c, !!x.excludesExternal)
    return { key: x.key, title: x.title, short: x.short, lpd: x.lpd, excludesExternal: !!x.excludesExternal, assessed: a, met: a <= x.lpd + EPS, margin: r1(x.lpd - a) }
  })

  const shareMap = new Map<PartGShare['group'], number>()
  for (const l of c.lines) shareMap.set(l.group, r2((shareMap.get(l.group) ?? 0) + l.lpd))
  const shares = [...shareMap.entries()].map(([group, lpd]) => ({ group, label: SHARE_LABELS[group], lpd }))

  return {
    occupancy,
    target: { ...t, value, custom },
    lines: c.lines,
    averaging: c.averaging,
    softener: c.softener,
    greywater: c.greywater,
    rainwater: c.rainwater,
    totalCalculatedUse: c.totalCalculatedUse,
    greywaterSaving: c.greywater.saving,
    rainwaterSaving: c.rainwater.saving,
    normalisationFactor: PARTG_NORMALISATION_FACTOR,
    normalised: c.normalised,
    external: PARTG_EXTERNAL_USE,
    total: c.total,
    assessed,
    excludesExternal,
    complies,
    headroom: r1(value - assessed),
    fittingsApproach: fittingsApproach(input, value),
    sensitivity,
    targets,
    shares,
    bathOnly: c.bathOnly,
    showerOnly: c.showerOnly,
    unconfirmed,
    warnings: [...new Set(warnings)],
  }
}

// ---------------------------------------------------------------- the cheapest way to meet the target

export type PartGGradeCategory = 'wc' | 'basin_tap' | 'kitchen_tap' | 'shower' | 'bath'

/**
 * Product grades for a fitting the drawings, SAP and the manufacturer leave open ('assumed'), from the standard grade
 * (the Table 2.2 maximum every assumed fitting starts at) to the most efficient widely sold, with a rough extra cost per
 * fitting over the standard grade (trade prices, 2026: a flow regulator or aerator costs a few pounds, an eco WC more).
 * Only for ranking: the optimiser picks the set with the lowest total extra cost that meets the target.
 */
export const PARTG_GRADES: Record<PartGGradeCategory, { value: number; partFlush?: number; extra: number; label: string }[]> = {
  wc: [
    { value: 4, partFlush: 2.6, extra: 0, label: '4/2.6 litre dual flush WC' },
    { value: 4, partFlush: 2, extra: 20, label: '4/2 litre dual flush WC' },
    { value: 3, partFlush: 2, extra: 60, label: '3/2 litre dual flush WC' },
  ],
  basin_tap: [
    { value: 5, extra: 0, label: '5 l/min basin taps' },
    { value: 4, extra: 4, label: '4 l/min basin taps (flow-limiting aerator)' },
    { value: 3, extra: 8, label: '3 l/min basin taps (flow-limiting aerator)' },
    { value: 1.7, extra: 25, label: '1.7 l/min eco basin taps' },
  ],
  kitchen_tap: [
    { value: 6, extra: 0, label: '6 l/min sink tap' },
    { value: 5, extra: 4, label: '5 l/min sink tap (flow-limiting aerator)' },
    { value: 4, extra: 8, label: '4 l/min sink tap (flow-limiting aerator)' },
  ],
  shower: [
    { value: 8, extra: 0, label: '8 l/min shower' },
    { value: 7, extra: 8, label: '7 l/min shower (flow regulator)' },
    { value: 6, extra: 12, label: '6 l/min shower (flow regulator)' },
    { value: 5, extra: 30, label: '5 l/min shower (aerated head)' },
  ],
  bath: [
    { value: 170, extra: 0, label: '170 litre bath (to overflow)' },
    { value: 155, extra: 15, label: '155 litre bath (to overflow)' },
    { value: 140, extra: 30, label: '140 litre shaped bath (to overflow)' },
  ],
}
const GRADE_CATS = Object.keys(PARTG_GRADES) as PartGGradeCategory[]
const OPT_NOTE = 'Chosen as the cheapest way to meet the target: '

export interface PartGOptimised {
  input: PartGInput
  /** the target is met (with the chosen grades) */
  met: boolean
  /** the assessed figure with the chosen grades, and the lowest the open fittings alone could reach */
  assessed: number
  best: number
  /** rough extra cost per dwelling over the standard grades (£) */
  extra: number
  /** the grade chosen for each category with open fittings */
  choices: { category: PartGGradeCategory; label: string; quantity: number; upgraded: boolean }[]
  /** what to tell the assessor */
  message: string
}

/**
 * The cheapest grades for the fittings nobody has specified ('assumed') that meet the input's target: every combination
 * of grades (one grade per category, a few hundred at most) is tried, and the lowest extra cost that complies wins (then
 * the fewest upgrades, then the most headroom). Assumed fittings always start again from the standard grade, so a
 * higher target puts them back. Fittings from the drawings, SAP or a manufacturer, appliances and water re-use are left
 * alone; if the open fittings can't reach the target, the most efficient grades are kept and the message says so.
 */
export function partgOptimise(raw: PartGInput): PartGOptimised {
  const input = partgNormaliseInput(raw)
  const occupancy = partgOccupancy(input)
  const t = partgTarget(input.targetKey) ?? PARTG_TARGETS[0]
  const value = input.targetLpd !== null && input.targetLpd > 0 ? input.targetLpd : t.lpd
  const assessedOf = (x: PartGInput) => {
    const c = core(x, occupancy)
    return t.excludesExternal ? c.normalised : c.total
  }
  const open = (f: PartGFitting) => f.source === 'assumed' && f.quantity > 0 && (GRADE_CATS as string[]).includes(f.category)
  const cats = GRADE_CATS.filter((c) => input.fittings.some((f) => f.category === c && open(f)))
  const qty = (c: PartGGradeCategory) => input.fittings.filter((f) => f.category === c && open(f)).reduce((a, f) => a + f.quantity, 0)
  const costOf = (pick: number[]) => pick.reduce((s, g, i) => s + PARTG_GRADES[cats[i]][g].extra * qty(cats[i]), 0)
  const withGrades = (pick: number[]): PartGInput => ({
    ...input,
    fittings: input.fittings.map((f) => {
      const i = cats.indexOf(f.category as PartGGradeCategory)
      if (i < 0 || !open(f)) return f
      const g = PARTG_GRADES[cats[i]][pick[i]]
      const own = (f.note ?? '').startsWith(OPT_NOTE) ? '' : (f.note ?? '')
      return {
        ...f,
        value: g.value,
        ...(f.category === 'wc' ? { partFlush: g.partFlush ?? null, dualFlush: true } : {}),
        note: pick[i] > 0 ? `${OPT_NOTE}${g.label} (to be confirmed).` : own || `${PARTG_GRADES[cats[i]][0].label}: the standard grade, to be confirmed.`,
      }
    }),
  })
  let best: { pick: number[]; cost: number; steps: number; assessed: number } | null = null
  let lowest = { pick: cats.map((c) => PARTG_GRADES[c].length - 1), assessed: Infinity }
  const pick = cats.map(() => 0)
  const visit = (k: number) => {
    if (k === cats.length) {
      const a = assessedOf(withGrades(pick))
      if (a < lowest.assessed) lowest = { pick: [...pick], assessed: a }
      if (a > value + EPS) return
      const cost = costOf(pick)
      const steps = pick.reduce((s, g) => s + g, 0)
      if (!best || cost < best.cost - EPS || (Math.abs(cost - best.cost) <= EPS && (steps < best.steps || (steps === best.steps && a < best.assessed))))
        best = { pick: [...pick], cost, steps, assessed: a }
      return
    }
    for (let g = 0; g < PARTG_GRADES[cats[k]].length; g++) {
      pick[k] = g
      visit(k + 1)
    }
    pick[k] = 0
  }
  visit(0)
  const found = best as { pick: number[]; cost: number; assessed: number } | null
  const chosen = found ?? { pick: lowest.pick, cost: costOf(lowest.pick), assessed: lowest.assessed }
  const choices = cats.map((c, i) => ({ category: c, label: PARTG_GRADES[c][chosen.pick[i]].label, quantity: qty(c), upgraded: chosen.pick[i] > 0 }))
  const upgrades = choices.filter((c) => c.upgraded)
  const f1 = (n: number) => String(r1(n))
  const now = assessedOf(input)
  const message = !cats.length
    ? now <= value + EPS
      ? 'Meets the target; every fitting is specified, so none was changed.'
      : `Over the target by ${f1(now - value)} l/p/d and every fitting is specified: change the products, or add rainwater or greywater re-use.`
    : found
      ? upgrades.length
        ? `Meets ${f1(value)} l/p/d at ${f1(chosen.assessed)} with ${upgrades.map((u) => u.label).join(', ')} (about £${Math.round(chosen.cost)} extra per dwelling; to be confirmed).`
        : `Meets ${f1(value)} l/p/d at ${f1(chosen.assessed)} with standard fittings.`
      : `Can’t reach ${f1(value)} l/p/d with efficient fittings alone (best ${f1(lowest.assessed)}): the most efficient grades are set; add rainwater or greywater re-use, or efficient appliances.`
  return { input: withGrades(chosen.pick), met: !!found || (!cats.length && now <= value + EPS), assessed: r1(cats.length ? chosen.assessed : now), best: r1(cats.length ? lowest.assessed : now), extra: Math.round(chosen.cost), choices, message }
}

/** What the regulation 37 notice states for this dwelling. */
export function partgNoticeStatement(r: PartGResult): string {
  return `The calculated potential consumption of wholesome water for this dwelling is ${r.total.toFixed(1)} litres/person/day (Approved Document G Appendix A water efficiency calculator).`
}

// ---------------------------------------------------------------- made from a Kaizen SAP dwelling

export interface PartGPrefill {
  input: PartGInput
  /** where things came from, in words, for the page and the report */
  notes: string[]
}

const LEVELS = ['Ground', 'First', 'Second', 'Third', 'Fourth', 'Fifth']
const levelName = (floor: unknown) => {
  const n = Number(floor)
  if (Number.isFinite(n) && String(floor).trim() !== '') return n >= 1 ? (LEVELS[n - 1] ?? `Floor ${n}`) : n === 0 ? 'Lower ground' : `Floor ${n}`
  const s = typeof floor === 'string' ? floor.trim() : ''
  return s ? s.replace(/\b\w/g, (m) => m.toUpperCase()) : ''
}
const titleCase = (s: string) => s.toLowerCase().replace(/(^|[\s/-])(\w)/g, (_m, a: string, b: string) => a + b.toUpperCase()).replace(/\bWc\b/g, 'WC')
const FLOW = /(\d+(?:\.\d+)?)\s*(?:l|lt|ltr|ltrs|litres?|liters?)\s*(?:\/|per\s*)\s*min/i
const CAPACITY = /(\d{2,3}(?:\.\d+)?)\s*(?:l|litres?|liters?)\b(?!\s*(?:\/|per)\s*min)/i

type WetKind = 'wc' | 'bathroom' | 'ensuite' | 'kitchen' | 'utility'
function wetKind(name: string): WetKind | null {
  const n = name.toLowerCase()
  if (/en[\s-]?suite|ensuite|shower\s*room/.test(n)) return 'ensuite'
  if (/bath/.test(n)) return 'bathroom'
  if (/\bwc\b|w\.c\.|cloak|toilet|lavatory/.test(n)) return 'wc'
  if (/kitchen/.test(n)) return 'kitchen'
  if (/utility/.test(n)) return 'utility'
  return null
}

interface ModelRoom { name: string; level: string; ids: string[] }

/**
 * A first Part G specification from a Kaizen SAP dwelling (sap_plots.input): bedrooms from the SAP dimensions, the wet
 * rooms, baths and showers from the SketchUp model inside it (SketchUpImport.SourceJson: rooms, services), shower flow
 * rates from the drawings or SAP. A figure nobody states starts at the Table 2.2 maximum, marked assumed; white goods
 * not provided take the Approved Document G defaults. Everything is editable afterwards.
 */
export function partgFromSap(sap: Record<string, unknown>): PartGPrefill {
  const input = partgDefaultInput()
  const notes: string[] = []
  const fittings: PartGFitting[] = []
  const dim = obj(sap.Dimension)
  let model: Record<string, unknown> | null = null
  const src = obj(sap.SketchUpImport).SourceJson
  if (typeof src === 'string' && src.trim().startsWith('{')) {
    try {
      model = obj(JSON.parse(src))
    } catch {
      model = null
    }
  }
  const rooms: ModelRoom[] = (Array.isArray(model?.rooms) ? (model!.rooms as unknown[]) : []).map((x) => {
    const r = obj(x)
    return { name: str(r.name, 120), level: levelName(r.floor), ids: (Array.isArray(obj(r.services).source_room_ids) ? (obj(r.services).source_room_ids as unknown[]) : []).map(String) }
  }).filter((r) => r.name)
  const services = obj(model?.services)
  const serviceRooms = new Map<string, { name: string; level: string }>()
  for (const x of Array.isArray(services.rooms) ? (services.rooms as unknown[]) : []) {
    const r = obj(x)
    if (r.id) serviceRooms.set(String(r.id), { name: titleCase(str(r.name, 120)), level: levelName(r.storey) })
  }
  const roomOf = (roomId: unknown) => {
    const id = String(roomId ?? '')
    const m = rooms.find((r) => r.ids.includes(id))
    if (m) return { name: m.name, level: m.level }
    return serviceRooms.get(id) ?? { name: '', level: '' }
  }
  const items = (Array.isArray(services.items) ? (services.items as unknown[]) : []).map(obj)

  // bedrooms
  const sapBeds = Number(dim.TotalBedrooms)
  const modelBeds = rooms.filter((r) => /bed/i.test(r.name) && !/bedroom\s*equiv/i.test(r.name)).length
  if (Number.isFinite(sapBeds) && sapBeds > 0) {
    input.bedrooms = Math.round(sapBeds)
    notes.push(`${input.bedrooms} bedroom${input.bedrooms === 1 ? '' : 's'} from the SAP assessment (occupancy ${partgOccupancy(input)}).`)
  } else if (modelBeds) {
    input.bedrooms = modelBeds
    notes.push(`${modelBeds} bedroom${modelBeds === 1 ? '' : 's'} counted on the SketchUp model.`)
  } else notes.push('The number of bedrooms was not found: enter it (it sets the occupancy for a softener or water re-use).')

  // WCs and basins in each WC, bathroom and en-suite; a sink tap in the kitchen and the utility room
  const wet = rooms.map((r) => ({ ...r, kind: wetKind(r.name) })).filter((r) => r.kind)
  const wcRooms = wet.filter((r) => r.kind === 'wc' || r.kind === 'bathroom' || r.kind === 'ensuite')
  for (const r of wcRooms) {
    fittings.push(partgNewFitting('wc', { reference: `${r.name} WC`, location: r.name, level: r.level, origin: 'model', note: 'Position from the SketchUp model; flush volumes are the Table 2.2 maximum until the product is confirmed.' }))
    fittings.push(partgNewFitting('basin_tap', { reference: `${r.name} basin taps`, location: r.name, level: r.level, origin: 'model', note: 'Position from the SketchUp model; flow rate is the Table 2.2 maximum until the product is confirmed.' }))
  }
  for (const r of wet.filter((x) => x.kind === 'kitchen' || x.kind === 'utility'))
    fittings.push(partgNewFitting('kitchen_tap', { reference: `${r.name} sink tap`, location: r.name, level: r.level, origin: 'model', note: r.kind === 'utility' ? 'A utility room is taken to have a sink; flow rate is the Table 2.2 maximum until confirmed.' : 'Flow rate is the Table 2.2 maximum until the product is confirmed.' }))
  if (wcRooms.length) notes.push(`${wcRooms.length} WC${wcRooms.length === 1 ? '' : 's'} and wash basin${wcRooms.length === 1 ? '' : 's'} from the SketchUp model’s ${wcRooms.map((r) => r.name).join(', ')}.`)

  const product = (it: Record<string, unknown>) => [str(it.manufacturer, 100), str(it.model, 100)].filter(Boolean).join(' ') || undefined
  const text = (it: Record<string, unknown>) => `${str(it.type, 400)} ${str(it.evidence, 1000)}`

  // baths
  const bathItems = items.filter((it) => it.category === 'bath')
  for (const it of bathItems) {
    const room = roomOf(it.room_id)
    const cap = Number(it.capacity_litres)
    const stated = Number.isFinite(cap) && cap > 20 ? cap : null
    const m = stated === null ? CAPACITY.exec(text(it)) : null
    const read = stated ?? (m && Number(m[1]) >= 80 && Number(m[1]) <= 400 ? Number(m[1]) : null)
    fittings.push(partgNewFitting('bath', {
      reference: `${room.name || 'Bathroom'} bath`, location: room.name || 'Bathroom', level: room.level, quantity: Math.max(1, int(it.quantity, 1, 1, 10)),
      value: read ?? PARTG_SEED.bath.value, source: read !== null ? 'drawings' : 'assumed', origin: 'model', product: product(it),
      note: read !== null ? `Capacity ${read} litres from the drawings: ${str(it.type, 120)}.` : `${str(it.type, 120) || 'Bath'} on the drawings; capacity to overflow is the Table 2.2 maximum until the product is confirmed.`,
    }))
  }
  const sapBaths = int(sap.TotalBaths, 0, 0, 10)
  if (!bathItems.length && sapBaths > 0) {
    for (let i = 0; i < sapBaths; i++) fittings.push(partgNewFitting('bath', { reference: `Bath ${sapBaths > 1 ? i + 1 : ''}`.trim(), location: wcRooms.find((r) => r.kind === 'bathroom')?.name ?? 'Bathroom', origin: 'sap', note: 'From the SAP assessment’s number of baths; capacity to overflow is the Table 2.2 maximum until confirmed.' }))
  }
  const nBaths = fittings.filter((f) => f.category === 'bath').reduce((s, f) => s + f.quantity, 0)
  if (nBaths) notes.push(`${nBaths} bath${nBaths === 1 ? '' : 's'} from ${bathItems.length ? 'the SketchUp model' : 'the SAP assessment'}.`)

  // showers: the drawings' flow rate, else SAP's, else the Table 2.2 maximum
  const sapShowers = (Array.isArray(sap.Showers) ? (sap.Showers as unknown[]) : []).map(obj)
  const sapRate = (room: string) => {
    const s = sapShowers.find((x) => room && `${str(x.Position)} ${str(x.ShowerName)}`.toLowerCase().includes(room.toLowerCase())) ?? (sapShowers.length === 1 ? sapShowers[0] : null)
    const r = s ? Number(s.Rate) : NaN
    return Number.isFinite(r) && r > 0 ? r : null
  }
  const showerItems = items.filter((it) => it.category === 'shower')
  for (const it of showerItems) {
    const room = roomOf(it.room_id)
    const m = FLOW.exec(text(it))
    const drawn = m ? Number(m[1]) : null
    const fromSap = drawn === null ? sapRate(room.name) : null
    const overBath = it.shower_location === 'over_bath' || /\bbath/i.test(str(it.type, 200))
    fittings.push(partgNewFitting('shower', {
      reference: `${room.name || 'Shower'} ${overBath ? 'over-bath shower' : 'shower'}`.trim(), location: room.name || '', level: room.level, quantity: Math.max(1, int(it.quantity, 1, 1, 10)),
      value: drawn ?? fromSap ?? PARTG_SEED.shower.value, source: drawn !== null ? 'drawings' : fromSap !== null ? 'sap' : 'assumed', origin: 'model', product: product(it),
      note: drawn !== null ? `${drawn} l/min stated on the drawings.` : fromSap !== null ? `${fromSap} l/min from the SAP assessment’s shower.` : `${str(it.type, 120) || 'Shower'}; flow rate is the Table 2.2 maximum until the product is confirmed.`,
    }))
  }
  if (!showerItems.length) {
    for (const s of sapShowers) {
      const pos = str(s.Position, 200) || str(s.ShowerName, 200)
      const roomName = pos.split(/\s+[—–-]\s+/)[0]?.trim() ?? ''
      const r = Number(s.Rate)
      const rate = Number.isFinite(r) && r > 0 ? r : null
      const wetRoom = wcRooms.find((x) => x.name.toLowerCase() === roomName.toLowerCase())
      fittings.push(partgNewFitting('shower', {
        reference: str(s.ShowerName, 200) || 'Shower', location: wetRoom?.name ?? roomName, level: wetRoom?.level, value: rate ?? PARTG_SEED.shower.value,
        source: rate !== null ? 'sap' : 'assumed', origin: 'sap', note: rate !== null ? `${rate} l/min from the SAP assessment.` : 'From the SAP assessment’s showers; flow rate is the Table 2.2 maximum until confirmed.',
      }))
    }
  }
  const nShowers = fittings.filter((f) => f.category === 'shower').reduce((s, f) => s + f.quantity, 0)
  if (nShowers) notes.push(`${nShowers} shower${nShowers === 1 ? '' : 's'} from ${showerItems.length ? 'the SketchUp model' : 'the SAP assessment'}; flow rates ${fittings.filter((f) => f.category === 'shower').map((f) => `${f.value} l/min`).join(', ')}.`)

  // a dwelling always has a WC, basin and sink: when the model has none, one of each is assumed
  if (!fittings.some((f) => f.category === 'wc')) {
    fittings.push(partgNewFitting('wc', { reference: 'WC', location: 'Bathroom', origin: 'standard', note: 'No WC found on the model: one is assumed; confirm the number and the product.' }))
    fittings.push(partgNewFitting('basin_tap', { reference: 'Basin taps', location: 'Bathroom', origin: 'standard', note: 'No wash basin found on the model: one is assumed; confirm.' }))
    notes.push('No wet rooms were found on a SketchUp model: one WC and wash basin are assumed. Add the dwelling’s WCs and basins.')
  }
  if (!fittings.some((f) => f.category === 'kitchen_tap')) fittings.push(partgNewFitting('kitchen_tap', { reference: 'Kitchen sink tap', location: 'Kitchen', origin: 'standard', note: 'Every dwelling has a kitchen sink; flow rate is the Table 2.2 maximum until confirmed.' }))
  if (!fittings.some((f) => f.category === 'bath' || f.category === 'shower')) {
    fittings.push(partgNewFitting('bath', { reference: 'Bath', location: 'Bathroom', origin: 'standard', note: 'No bath or shower was found: a bath with a shower over is assumed (the commoner and higher-consuming arrangement); confirm.' }))
    fittings.push(partgNewFitting('shower', { reference: 'Over-bath shower', location: 'Bathroom', origin: 'standard', note: 'Assumed with the bath; confirm.' }))
    notes.push('No bath or shower was found: a bath with a shower over it is assumed.')
  }

  // white goods: the Approved Document G defaults where not provided
  const kitchen = wet.find((r) => r.kind === 'kitchen')
  const utility = wet.find((r) => r.kind === 'utility')
  fittings.push(partgNewFitting('washing_machine', { reference: 'Washing machine (not provided)', location: utility?.name ?? kitchen?.name ?? 'Kitchen', level: (utility ?? kitchen)?.level, origin: 'standard', note: 'Paragraph A2f-ii: 8.17 litres/kg where the washing machine is not provided. Replace with the energy label figure if one is fitted.' }))
  fittings.push(partgNewFitting('dishwasher', { reference: 'Dishwasher (not provided)', location: kitchen?.name ?? 'Kitchen', level: kitchen?.level, origin: 'standard', note: 'Paragraph A2e-ii: 1.25 litres/place setting where the dishwasher is not provided. Replace with the energy label figure if one is fitted.' }))

  input.fittings = fittings
  if (fittings.some((f) => f.source === 'assumed')) notes.push('Figures not stated on the drawings or in SAP start at the Approved Document G Table 2.2 maximum (the 110 l/p/d fittings approach) and are marked assumed until the products are confirmed.')
  return { input, notes }
}
